//components
import IDMSession from './src/lib/IDMSession';
import IDMImpersonate from './src/lib/IDMImpersonate';
import URLManager from './src/Wrappers/URLManager';
//interfaces
import IDMAuthenticator from './src/interfaces/IDMAuthenticator';
import IDMRegistration from './src/interfaces/IDMRegistration';
import IDMExternalProvider from './src/interfaces/IDMExternalProvider';
import IDMUser from './src/interfaces/IDMUser';
import API from './src/API/API';
//exported components
import AppIDv2 from './src/Wrappers/AppIDv2';
import AppID from './src/Wrappers/legacy/AppID';
import IDMGlobals from './src/IDMGlobals';
import IDMUtils from './src/IDMUtils';
//
import packageJSON from './package.json';
//
export default class IDM {
  /**
   * @constructs
   * IDM Client module constructor, you should use one instance of
   * this client per application instance, if multiple instances are used incorrect
   * behavior is expected. This client is instantiate with the access tokens (key and secret),
   * and options that will dictate the module behaviour.
   **/
  constructor(_accessKey, _accessSecret, opts) {
    if (!opts || !opts.disableLogs) console.debug(`IDM Module - version: ${packageJSON.version}`);
    this._accessKey = _accessKey;
    this._accessSecret = _accessSecret;
    this.opts = (opts ? opts : {});

    //Public callbacks
      //Function to be called whenever a session is loaded with error.
    this.sessionLoadErrorHandler = null;
      //Function to be called whenever a session is loaded properly and authenticated.
    this.sessionLoadedHandler = null;
      //Function to be called whenever a external provider configuration is loaded and ready to go
    this.sessionExternalProviderLoadedHandler = null;

    //Public
    this.session = new IDMSession(this);
    this.api = new API(this);
    this.urlmanager = new URLManager(this);
    this.impersonate = new IDMImpersonate(this);
    //Interfaces
    this.registration = new IDMRegistration(this);
    this.authenticator = new IDMAuthenticator(this);
    this.user = new IDMUser(this);
    this.externalProviders = new IDMExternalProvider(this);
  }

  /**
   * async load - Asynchrounsly load a session, by checking if any previous validation
   * session if valid by loading the local session, and revalidating the session
   * remotely to check if any new restrictions of information from that user,
   * Check if valid, check if impersonating a new role (if so, auto enroll this user into),
   * this role and remove the role impersonation.
   *
   * @return { Boolean }  Return API response from validation call or enrollment if any
   * previous valid session, if not, will return undefined or null.
   */
  async load(optLoginURL, optExtraContext) { return ((await this.session.loadSession(false, optLoginURL, optExtraContext)) && this.isLogged()); }

  //Getters
  getAccessKey() { return this._accessKey; }
  getAccessSecret() { return this._accessSecret; }
  isLogged() { return this.session.isLogged(); }
  async currentAppIDHash(allowsAppImpersonation) {
    //If is coming from other source, use it's app id if valid - but asking for role enrollement
    if (allowsAppImpersonation && this.impersonate.isImpersonatingApp()) {
      if (!this.opts || !this.opts.disableLogs) console.debug('using impersonate appid');
      return this.impersonate.getApp();
    }
    //Get pk
    const pk = await this.session.getPublicToken();
    if (!pk) return null;
    //Return default appid
    return (new AppIDv2(null, this._accessKey, this._accessSecret, pk)).getHash();
  }

  /* internal handlers */
  /**
   * async handleAuthenticationResponse - Async call to process authentication API calls from IDM server.
   * This method will parse the request and if authentication is succeeded will load and save session (if specified),
   * If authentication failed, it will clear the local session and notifiy the sessionLoadErrorHandler, if any set.
   */
  async handleAuthenticationResponse(resp, username, isRenewal, isLocalAuthentication) {
    //check if successfull - if so save tokens
    if (isLocalAuthentication || (resp.statusCode == 200 && resp.body && resp.body.challengeState == IDMGlobals.AuthorizationChallengeState_Authorized)) {
      //This flag guarantees no double session loaded handler is called when auto enroll is performed
      //and therefore a secondary session load has already called it
      let secondarySessionReloadHappened = false;
      if (!isLocalAuthentication) {
        //Load current sesion before possible redirect
        await this.session.setSessionWithData(resp.body);
        //include renewal token in case of revalidation and response does not contains it
        if (resp.body && !resp.body.renewalToken) resp.body.renewalToken = await this.session.data.getRenewalToken();
        //Post login process
        secondarySessionReloadHappened = await this.postLoginProcess(resp.body.redirectURL);
      } else {
        //Post login process
        secondarySessionReloadHappened = await this.postLoginProcess();
      }
      //Load session callback
      if (this.sessionLoadedHandler && !secondarySessionReloadHappened) await this.sessionLoadedHandler(isRenewal, isLocalAuthentication);
    } else {
      await this.session.clearSession(); //Error, we should clear current session, may it be the cause?
      //Fail load session callback
      if (this.sessionLoadErrorHandler) await this.sessionLoadErrorHandler(resp, username, isRenewal);
    }
  }
  /**
   * async handleImpersonateResponseAndReloadSession - Process impersonation API calls
   * response (as registration and enrollment), if valid, revalidate session and follow
   * the normal authentication flow.
   */
  async handleImpersonateResponseAndReloadSession(resp) {
    //check if successfull
    if (resp && resp.statusCode == 200) {
      //remove impersonation
      this.impersonate.unpersonateRole();
      //revalidate session with new enrollment
      if (this.isLogged()) await this.session.loadSession(true);
    }
  }
  //
  async postLoginProcess(redirectURL) {
    let resp = null;
    //impersonate allows it, send enrollment request
    if (this.impersonate.allowsEnrollment()) {
      if (!this.opts || !this.opts.disableLogs) console.debug('Enrolling with role(s)', this.impersonate.getRole());
      resp = await this.registration.enroll({
        username: this.session.authorization.getUserEmail(),
        role: this.impersonate.getRole()
      });
    }
    //redirect back -- return 
    const enableSSOCodeOnRedirects = (this.opts && this.opts.enableSSOCodeOnRedirects);
    if (this.impersonate.isImpersonatingCBURL()) {
      await this.urlmanager.redirectBack(this.impersonate.getCallbackURL(), enableSSOCodeOnRedirects)
      return true; //ask for interruption
    }
    else if (redirectURL) {
      await this.urlmanager.redirectBack(redirectURL, enableSSOCodeOnRedirects);
      return true; //ask for interruption
    }
    //remove impersonation
    this.impersonate.unpersonate();
    //
    return !!resp; //ask for interruption when enroll is performed (it does spawn another load session proc.)
  }
}
//
export { IDMGlobals, AppIDv2, AppID, IDMUtils };
